import React, { useEffect } from 'react'
import { getDefaultToken, initialPresentationalState, MAX_PRICE_IMPACT_PERCENT } from './defaults'
import {
  PresentationalStateActions,
  PriceImpactState,
  TransactionalContextProviderProps,
  TransactionalState,
} from './interface'
import { presentationalStateReducer } from './reducer'

import { assetsListBySymbol } from 'abis/set/asset-icons'
import Big from 'big.js'
import { getProductByAddress } from 'indices-list'
import { useParams } from 'react-router'
import { ProductType } from 'sdk'
import { useStore } from 'services/store'

const TransactionalContext = React.createContext<TransactionalState>(initialPresentationalState)

export const TransactionalContextProvider: React.FC<TransactionalContextProviderProps> = ({
  children,
}) => {
  const { network } = useStore()
  const { symbol } = useParams<{ symbol: string }>()
  const address = assetsListBySymbol[symbol?.toUpperCase() as string].address
  const { decimals } = getProductByAddress(address)
  /**
   * JIT: Variable settings
   */

  initialPresentationalState.receivedTokens = [getDefaultToken(address, decimals)]
  // set up information about the product
  initialPresentationalState.productAddress = address.toLocaleLowerCase()
  initialPresentationalState.productType =
    getProductByAddress(address)?.productType || ProductType.INDEX_V1

  // initialPresentationalState.redemptionAsset = redemptionAssets[0]

  const [presentationalState, dispatch] = React.useReducer(
    presentationalStateReducer,
    initialPresentationalState,
  )
  /**
   * IT: Changes the selected asset to default
   * WHEN: The network changes
   * WHY: Because the selected asset must be correct before the widget fully renders
   */
  useEffect(() => {
    // dispatch({
    //   type: PresentationalStateActions.SET_SELECTED_ASSET,
    //   payload: getProductByAddress(address).defaultSelectedAsset,
    // })
  }, [network])

  /**
   * IT: Checks the current price impact against the max price impact
   * WHEN: The current price impact changes and the user has not acknowledged the warning
   */
  useEffect(() => {
    if (Big(presentationalState.currentPriceImpactPercentage).eq(0)) {
      dispatch({ type: PresentationalStateActions.RESET_PRICE_IMPACT })
    }
    if (
      Big(presentationalState.currentPriceImpactPercentage).lt(
        Big(MAX_PRICE_IMPACT_PERCENT).mul(100),
      ) &&
      presentationalState.priceImpactState !== PriceImpactState.ACKNOWLEDGED
    ) {
      dispatch({ type: PresentationalStateActions.WARN_PRICE_IMPACT })
    } else if (presentationalState.priceImpactState !== PriceImpactState.ACKNOWLEDGED) {
      dispatch({ type: PresentationalStateActions.RESET_PRICE_IMPACT })
    }
  }, [presentationalState.priceImpactState, presentationalState.currentPriceImpactPercentage])

  useEffect(() => {
    dispatch({ type: PresentationalStateActions.RESET_PRICE_IMPACT })
  }, [presentationalState.isBuying])
  /**
   * IT: sets the price impact as a percentage change (not percentage difference)
   * WHEN: the dollar values change
   */
  useEffect(() => {
    if (!presentationalState.inputDollarValue || Big(presentationalState.inputDollarValue).eq(0)) {
      dispatch({ type: PresentationalStateActions.SET_PRICE_IMPACT, payload: '0' })
    } else if (presentationalState.outputDollarValue) {
      const input = Big(presentationalState.inputDollarValue)
      const output = Big(presentationalState.outputDollarValue)

      if (input.gt(0) && output.gt(0)) {
        const change = output.minus(input).div(input).mul(100).toPrecision(2)
        dispatch({ type: PresentationalStateActions.SET_PRICE_IMPACT, payload: change })
      }
    }
  }, [presentationalState.inputDollarValue, presentationalState.outputDollarValue])

  return (
    <TransactionalContext.Provider value={{ ...presentationalState, ...{ dispatch } }}>
      {children}
    </TransactionalContext.Provider>
  )
}

export const useTransactionalContext = (): TransactionalState => {
  const context = React.useContext(TransactionalContext)
  if (context === undefined) {
    throw new Error('useTransactionalContext must be used within a TransactionalContextProvider')
  }
  return context
}
